<template>
  <div class="admin-termin">
    <header class="admin-head">
      <div class="admin-head__row">
        <h1>Termin-Verwaltung</h1>
        <button class="btn btn--sm admin-logout" @click="logout">Abmelden</button>
      </div>
      <nav class="admin-tabs">
        <button :class="{active: tab==='bookings'}" @click="setTab('bookings')">Beratungswünsche</button>
        <button :class="{active: tab==='calls'}" class="tab-calls" @click="setTab('calls')">
          Anrufe
          <span v-if="vm.counts.neu" class="tab-calls__badge">{{ vm.counts.neu }}</span>
        </button>
        <button class="tab-calls" @click="$router.push({ name: 'admin-mail' })">
          Inbox
          <span v-if="mailUnread" class="tab-calls__badge">{{ mailUnread }}</span>
        </button>
        <button :class="{active: tab==='slots'}"    @click="setTab('slots')">Slots</button>
        <button :class="{active: tab==='texts'}"    @click="setTab('texts')">Mail-/Seitentexte</button>
        <button :class="{active: tab==='settings'}" @click="setTab('settings')">Einstellungen</button>
      </nav>
    </header>

    <!-- ============ BOOKINGS / BERATUNGSWÜNSCHE ============ -->
    <section v-if="tab === 'bookings'" class="admin-section">
      <div class="admin-toolbar">
        <button class="btn" @click="loadBookings">Neu laden</button>
      </div>
      <table v-if="bookings.length" class="admin-table">
        <thead>
          <tr>
            <th>Termin</th><th>Name</th><th>E-Mail</th><th>Telefon</th>
            <th>Nachricht</th><th>Quelle</th><th>Status</th><th></th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="b in bookings"
            :key="b.booking_id"
            class="booking-row"
            title="Details öffnen"
            @click="openDetail(b)"
          >
            <td>{{ b.date }} {{ b.time }}</td>
            <td class="booking-row__name">
              {{ b.name }} <span class="booking-row__arrow">→</span>
              <small v-if="b.birthdate || b.insurance_label" class="booking-row__sub">
                <template v-if="b.birthdate">geb. {{ b.birthdate }}</template>
                <template v-if="b.birthdate && b.insurance_label"> · </template>
                {{ b.insurance_label }}
              </small>
            </td>
            <td>{{ b.email }}</td>
            <td>{{ b.phone }}</td>
            <td class="td-msg">{{ b.message }}</td>
            <td>
              <span v-if="b.source" class="source-badge">{{ b.source }}</span>
              <span v-else>—</span>
            </td>
            <td>{{ statusLabel(b.status) }}</td>
            <td class="td-actions" @click.stop>
              <button
                v-if="b.phone"
                class="btn btn--sm btn--call"
                title="Kunden jetzt anrufen"
                @click="openPhone(b)"
              >📞 anrufen</button>
              <button class="btn btn--sm" :disabled="b.status===1" @click="confirmBooking(b)">bestätigen</button>
              <button class="btn btn--sm btn--danger" :disabled="b.status===2" @click="cancelBooking(b)">absagen</button>
              <button class="btn btn--sm btn--danger" @click="deleteBooking(b)">löschen</button>
            </td>
          </tr>
        </tbody>
      </table>
      <p v-else class="admin-empty">Noch keine Beratungswünsche.</p>
    </section>

    <!-- ============ ANRUFE (Anrufbeantworter, brain-DB company 9) ============ -->
    <section v-if="tab === 'calls'" class="admin-section">
      <!-- Ansage-Editor (cal_setting voicemail_greeting, gesprochen von
           Twilio-TTS beim Anruf auf +49 89 620 833 72) -->
      <details class="vm-greeting">
        <summary>📢 Anrufbeantworter-Ansage bearbeiten</summary>
        <p class="admin-help">
          Dieser Text wird Anrufern auf <strong>+49 89 620 833 72</strong> vorgelesen
          (deutsche Computerstimme), danach kommt der Signalton und die Aufnahme.
          Änderungen gelten sofort für den nächsten Anruf.
        </p>
        <textarea v-model="vmGreeting" rows="10" class="vm-greeting__text"></textarea>
        <button class="btn btn--primary" :disabled="vmGreetingSaving" @click="saveVmGreeting">
          {{ vmGreetingSaving ? "Speichern…" : "Ansage speichern" }}
        </button>
      </details>

      <div class="admin-toolbar">
        <div class="vm-chips">
          <button
            v-for="st in vmStatusTabs"
            :key="st.key"
            class="vm-chip"
            :class="{ 'vm-chip--active': vm.status === st.key }"
            @click="vm.status = st.key; loadVm()"
          >
            {{ st.label }}
            <span class="vm-chip__count">{{ vm.counts[st.key] ?? 0 }}</span>
          </button>
        </div>
        <input
          v-model="vm.q"
          type="search"
          class="vm-search"
          placeholder="Nummer, Notiz, Transkript…"
          @keyup.enter="loadVm"
        />
        <button class="btn" @click="loadVm">Neu laden</button>
      </div>

      <p v-if="vm.error" class="admin-empty">⚠️ {{ vm.error }}</p>
      <p v-else-if="!vm.calls.length" class="admin-empty">
        {{ vm.loading ? "Lade Anrufe…" : "Keine Anrufe in dieser Ansicht." }}
      </p>

      <article v-for="c in vm.calls" :key="c.call_id" class="vm-card">
        <div v-if="c.locked" class="vm-lock" :class="c.locked_by_me ? 'vm-lock--me' : 'vm-lock--other'">
          <template v-if="c.locked_by_me">
            Du rufst gerade an.
            <button class="btn btn--sm" @click="vmUnlock(c)">Anruf beendet</button>
          </template>
          <template v-else>
            📞 {{ c.lock_name || "Jemand" }} ruft gerade an (seit {{ fmtVmTime(c.lock_at) }})
            <button class="btn btn--sm btn--danger" @click="vmCall(c, true)">trotzdem übernehmen</button>
          </template>
        </div>

        <div class="vm-card__head">
          <div class="vm-card__who">
            <span class="vm-status" :class="'vm-status--' + c.vm_status">{{ vmStatusLabel(c.vm_status) }}</span>
            <strong class="vm-card__number">{{ c.from_number || "unbekannt" }}</strong>
            <span v-if="c.source" class="source-badge">{{ c.source }}</span>
          </div>
          <div class="vm-card__meta">
            {{ fmtVmTime(c.started_at) }}
            <span v-if="c.duration_sec"> · {{ fmtDur(c.duration_sec) }}</span>
          </div>
        </div>

        <p v-if="c.callback_wish" class="vm-card__wish">
          ⏰ Rückrufwunsch: <strong>{{ c.callback_wish }}</strong>
        </p>

        <div class="vm-card__transcript">
          <template v-if="c.transcript_text">{{ c.transcript_text }}</template>
          <em v-else-if="c.error" class="vm-card__err">{{ c.error }}</em>
          <em v-else>Transkript wird erstellt …</em>
        </div>

        <div class="vm-card__actions">
          <button
            v-if="c.from_number"
            class="btn btn--sm btn--call"
            :disabled="c.locked && !c.locked_by_me"
            @click="vmCall(c)"
          >📞 zurückrufen</button>
          <select
            class="vm-select"
            :value="c.vm_status"
            @change="vmSetStatus(c, $event.target.value)"
          >
            <option value="neu">neu</option>
            <option value="in_arbeit">in Arbeit</option>
            <option value="erledigt">erledigt</option>
          </select>
          <button
            v-if="c.vm_status !== 'erledigt'"
            class="btn btn--sm"
            @click="vmSetStatus(c, 'erledigt')"
          >✓ erledigt</button>
        </div>

        <div class="vm-card__note">
          <textarea
            v-model="vmNoteDrafts[c.call_id]"
            rows="2"
            placeholder="Gesprächsnotiz …"
          ></textarea>
          <button
            class="btn btn--sm"
            :disabled="(vmNoteDrafts[c.call_id] || '') === (c.note || '')"
            @click="vmSaveNote(c)"
          >Notiz speichern</button>
        </div>
      </article>
    </section>

    <!-- ============ SLOTS ============ -->
    <section v-if="tab === 'slots'" class="admin-section">
      <div class="admin-toolbar">
        <label>Von <input type="date" v-model="slotRange.from" @change="loadSlots"></label>
        <label>Bis <input type="date" v-model="slotRange.to"   @change="loadSlots"></label>
        <button class="btn" @click="loadSlots">Neu laden</button>
      </div>

      <h3>Neuen Slot anlegen</h3>
      <div class="admin-newslot">
        <label>Datum <input type="date" v-model="newSlot.date"></label>
        <label>Uhrzeit <input type="time" v-model="newSlot.time"></label>
        <label>Dauer (Min)
          <input type="number" v-model.number="newSlot.duration" min="5" max="480">
        </label>
        <label>Notiz <input type="text" v-model="newSlot.note"></label>
        <label class="admin-newslot__check">
          <input type="checkbox" v-model="newSlot.markBooked">
          <span>Direkt als gebucht markieren</span>
        </label>
        <button class="btn btn--primary" @click="addSlot">+ Slot</button>
      </div>

      <h3>Mehrere Slots auf einmal anlegen</h3>
      <p class="admin-help">
        Sie können hier in einem <strong>Datums-Bereich</strong> auf einmal mehrere Slots
        generieren — z.B. <em>Mo–Fr von 09:00 bis 17:00 alle 30 Min.</em>
        Standard ist eine Slot-Dauer von <strong>30 Minuten</strong>.
        Bereits vorhandene Slots werden automatisch übersprungen.
      </p>
      <div class="admin-newslot admin-newslot--bulk">
        <label>Von Datum <input type="date" v-model="bulk.dateFrom"></label>
        <label>Bis Datum <input type="date" v-model="bulk.dateTo"></label>
        <label>Von Uhrzeit <input type="time" v-model="bulk.start"></label>
        <label>Bis Uhrzeit <input type="time" v-model="bulk.end"></label>
        <label>Slot-Dauer (Min)
          <input type="number" v-model.number="bulk.duration" min="5" max="480">
        </label>
      </div>
      <div class="admin-weekdays">
        <span class="admin-weekdays__label">Wochentage:</span>
        <label v-for="(name, idx) in weekdayNames" :key="idx" class="admin-weekday">
          <input type="checkbox" :value="idx" v-model="bulk.weekdays">
          <span>{{ name }}</span>
        </label>
        <button class="btn btn--primary admin-weekdays__action" @click="addBulk">
          + Slots generieren
        </button>
      </div>
      <div v-if="bulkSummary" class="admin-bulk-summary">
        <p><strong>{{ bulkSummary.created }}</strong> Slots angelegt.</p>
        <p v-if="bulkSummary.skipped && bulkSummary.skipped.length">
          <strong>{{ bulkSummary.skipped.length }}</strong> übersprungen (bereits vorhanden):
          <span class="admin-bulk-summary__list">{{ bulkSummary.skipped.join(", ") }}</span>
        </p>
        <p v-if="bulkSummary.errors && bulkSummary.errors.length" class="admin-bulk-summary__err">
          Fehler: {{ bulkSummary.errors.join(" | ") }}
        </p>
      </div>

      <h3>Woche im Voraus vorbereiten</h3>
      <p class="admin-help">
        Legt für die gewählte Woche die <strong>Default-Slots</strong> an (bereits
        vorhandene werden übersprungen) und markiert danach <strong>zufällig</strong>
        so viele freie Slots als „extern gebucht", bis die eingegebene
        <strong>Gesamtzahl belegter Slots</strong> für die Woche erreicht ist.
        Bereits gebuchte oder belegte Slots zählen dabei mit.
      </p>
      <div class="admin-newslot admin-newslot--bulk">
        <label>Woche (Datum in der Woche) <input type="date" v-model="week.start"></label>
        <label>Von Uhrzeit <input type="time" v-model="week.timeFrom"></label>
        <label>Bis Uhrzeit <input type="time" v-model="week.timeTo"></label>
        <label>Slot-Dauer (Min)
          <input type="number" v-model.number="week.duration" min="5" max="480">
        </label>
        <label>Belegte Slots gesamt (Woche)
          <input type="number" v-model.number="week.bookedTotal" min="0" max="500">
        </label>
      </div>
      <div class="admin-weekdays">
        <span class="admin-weekdays__label">Wochentage:</span>
        <label v-for="wd in isoWeekdays" :key="wd.iso" class="admin-weekday">
          <input type="checkbox" :value="wd.iso" v-model="week.weekdays">
          <span>{{ wd.name }}</span>
        </label>
        <button class="btn btn--primary admin-weekdays__action" @click="prepareWeek">
          ⚡ Woche vorbereiten
        </button>
      </div>
      <div v-if="weekSummary" class="admin-bulk-summary">
        <p>
          Woche ab <strong>{{ weekSummary.week_start }}</strong>:
          <strong>{{ weekSummary.created }}</strong> Default-Slots neu angelegt<span
            v-if="weekSummary.skipped"> ({{ weekSummary.skipped }} waren schon da)</span>.
        </p>
        <p>
          <strong>{{ weekSummary.booked_marked }}</strong> Slots zufällig als belegt markiert —
          vorher belegt: {{ weekSummary.booked_before }},
          Ziel: {{ weekSummary.booked_total }},
          jetzt frei: {{ weekSummary.free_after }}.
        </p>
        <p v-if="weekSummary.booked_before >= weekSummary.booked_total && !weekSummary.booked_marked">
          Die Zielzahl war bereits erreicht — es wurde nichts zusätzlich belegt.
        </p>
        <p v-if="weekSummary.errors && weekSummary.errors.length" class="admin-bulk-summary__err">
          Fehler: {{ weekSummary.errors.join(" | ") }}
        </p>
      </div>

      <h3>Automatik: jede Woche feste Zeiten und Belegung</h3>
      <p class="admin-help">
        Wenn aktiviert, wird die <strong>laufende Woche plus die nächsten Wochen</strong>
        automatisch mit den festen Vorgaben vorbereitet: fehlende Default-Slots werden
        angelegt und pro Woche so viele freie Slots zufällig als „extern gebucht" markiert,
        bis die Zielzahl erreicht ist. Läuft <strong>einmal täglich</strong> beim ersten
        Aufruf der Buchungsseite — ohne weiteres Zutun. Slots in der Vergangenheit bleiben unberührt.
      </p>
      <div class="admin-newslot admin-newslot--bulk">
        <label class="admin-newslot__check admin-auto__switch">
          <input type="checkbox" v-model="auto.enabled">
          <span><strong>Automatik aktiv</strong></span>
        </label>
        <label>Von Uhrzeit <input type="time" v-model="auto.time_from"></label>
        <label>Bis Uhrzeit <input type="time" v-model="auto.time_to"></label>
        <label>Slot-Dauer (Min)
          <input type="number" v-model.number="auto.duration" min="5" max="480">
        </label>
        <label>Belegte Slots pro Woche
          <input type="number" v-model.number="auto.booked_total" min="0" max="500">
        </label>
        <label>Wochen im Voraus
          <input type="number" v-model.number="auto.weeks_ahead" min="1" max="8">
        </label>
      </div>
      <div class="admin-weekdays">
        <span class="admin-weekdays__label">Wochentage:</span>
        <label v-for="wd in isoWeekdays" :key="'auto' + wd.iso" class="admin-weekday">
          <input type="checkbox" :value="wd.iso" v-model="auto.weekdays">
          <span>{{ wd.name }}</span>
        </label>
        <button class="btn btn--primary admin-weekdays__action" @click="saveAuto" :disabled="autoBusy">
          Automatik speichern
        </button>
        <button class="btn admin-weekdays__action" @click="runAuto" :disabled="autoBusy">
          ▶ Jetzt ausführen
        </button>
      </div>
      <div class="admin-bulk-summary admin-auto__status">
        <p v-if="autoConfig.enabled">
          <strong>Automatik ist aktiv</strong> — {{ autoConfig.weeks_ahead }} Woche(n) im Voraus,
          Ziel {{ autoConfig.booked_total }} belegte Slots pro Woche.
        </p>
        <p v-else><strong>Automatik ist aus.</strong> Einstellungen werden gespeichert, aber nicht ausgeführt.</p>
        <p v-if="autoConfig.last_run">Letzter Lauf: {{ autoConfig.last_run }}</p>
        <p v-else>Noch nie gelaufen.</p>
        <template v-if="autoConfig.last_result && autoConfig.last_result.weeks">
          <p v-for="w in autoConfig.last_result.weeks" :key="w.week_start">
            Woche ab <strong>{{ w.week_start }}</strong>:
            {{ w.created }} Slots angelegt, {{ w.booked_marked }} zusätzlich belegt
            (belegt vorher {{ w.booked_before }}, frei jetzt {{ w.free_after }})<span
              v-if="w.errors" class="admin-bulk-summary__err">, {{ w.errors }} Fehler</span><span
              v-if="w.message" class="admin-bulk-summary__err"> — {{ w.message }}</span>.
          </p>
        </template>
      </div>

      <table v-if="slots.length" class="admin-table">
        <thead>
          <tr>
            <th>Datum</th><th>Zeit</th><th>Dauer</th>
            <th>Status</th><th>Buchung</th><th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="s in slots" :key="s.slot_id"
              :class="{
                'is-booked-red': s.booking_id || s.is_marked_booked,
                'is-blocked':    s.is_blocked,
              }">
            <td>{{ s.date }}</td>
            <td>{{ s.time }}</td>
            <td>{{ s.duration }}'</td>
            <td>
              <span v-if="s.is_blocked">gesperrt</span>
              <span v-else-if="s.is_marked_booked" class="status-booked">extern gebucht</span>
              <span v-else-if="s.booking_id" class="status-booked">gebucht</span>
              <span v-else>aktiv</span>
            </td>
            <td>
              <span v-if="s.booking_id">{{ s.booking_name }} ({{ s.booking_email }})</span>
              <span v-else-if="s.is_marked_booked"><em>extern markiert</em></span>
              <span v-else>—</span>
            </td>
            <td class="td-actions">
              <button v-if="s.is_marked_booked" class="btn btn--sm" @click="releaseSlot(s)">wieder freigeben</button>
              <button v-else-if="!s.is_blocked" class="btn btn--sm" @click="blockSlot(s)">sperren</button>
              <button v-else class="btn btn--sm" @click="unblockSlot(s)">aktivieren</button>
              <button class="btn btn--sm btn--danger" @click="deleteSlot(s)" :disabled="!!s.booking_id">löschen</button>
            </td>
          </tr>
        </tbody>
      </table>
      <p v-else class="admin-empty">Keine Slots im gewählten Zeitraum.</p>
    </section>

    <!-- ============ TEXTS ============ -->
    <section v-if="tab === 'texts'" class="admin-section">
      <p class="admin-help">
        Platzhalter:
        <code>{booking_name}</code>, <code>{booking_email}</code>, <code>{booking_phone}</code>,
        <code>{booking_message}</code>, <code>{slot_date}</code>, <code>{slot_time}</code>,
        <code>{slot_duration}</code>
      </p>
      <article v-for="t in texts" :key="t.text_id" class="admin-text">
        <header>
          <strong>{{ t.text_description || t.text_key }}</strong>
          <small>({{ t.text_kind }} / {{ t.text_key }})</small>
        </header>
        <label v-if="t.text_kind === 'mail'">
          Betreff
          <input v-model="t.text_subject" type="text">
        </label>
        <label>
          Inhalt
          <textarea v-model="t.text_body" rows="6"></textarea>
        </label>
        <button class="btn btn--primary" @click="saveText(t)">Speichern</button>
      </article>
    </section>

    <!-- ============ SETTINGS ============ -->
    <section v-if="tab === 'settings'" class="admin-section">
      <div v-for="(value, key) in settings" :key="key" class="admin-setting">
        <label>
          <span class="admin-setting__label">{{ key }}</span>
          <input v-model="settings[key]" type="text">
        </label>
      </div>
      <button class="btn btn--primary" @click="saveSettings">Einstellungen speichern</button>
    </section>

    <div v-if="flash" class="admin-flash">{{ flash }}</div>

    <PhoneDialog
      v-if="phoneDialog.open"
      :phone="phoneDialog.phone"
      :booking-id="phoneDialog.bookingId"
      :contact-name="phoneDialog.name"
      @close="phoneDialog.open = false"
    />
  </div>
</template>

<script>
import BaseCalls from "@/services/BaseCalls.js";
import { useUserStore } from "@/stores/userStore.js";
import PhoneDialog from "@/components/sites/admin/PhoneDialog.vue";

function todayIso(offset = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offset);
  return d.toISOString().slice(0, 10);
}

function localIso(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** Montag der NÄCHSTEN Woche (Default für "Woche vorbereiten"). */
function nextMondayIso() {
  const d = new Date();
  d.setDate(d.getDate() + (((8 - d.getDay()) % 7) || 7));
  return localIso(d);
}

export default {
  name: "AdminTerminIndex",
  components: { PhoneDialog },
  data() {
    return {
      phoneDialog: { open: false, phone: "", bookingId: 0, name: "" },
      tab: "bookings",
      slotRange: { from: todayIso(0), to: todayIso(14) },
      slots: [],
      bookings: [],
      texts: [],
      settings: {},
      newSlot: { date: todayIso(0), time: "09:00", duration: 30, note: "", markBooked: false },
      bulk: {
        dateFrom: todayIso(0),
        dateTo:   todayIso(13),
        start:    "09:00",
        end:      "17:00",
        duration: 30,
        weekdays: [1, 2, 3, 4, 5], // Mo..Fr per Default
      },
      weekdayNames: ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"],
      bulkSummary: null,
      week: {
        start:       nextMondayIso(),
        timeFrom:    "09:00",
        timeTo:      "17:00",
        duration:    30,
        weekdays:    [1, 2, 3, 4, 5], // ISO: 1 = Mo
        bookedTotal: 0,
      },
      isoWeekdays: [
        { iso: 1, name: "Mo" }, { iso: 2, name: "Di" }, { iso: 3, name: "Mi" },
        { iso: 4, name: "Do" }, { iso: 5, name: "Fr" }, { iso: 6, name: "Sa" },
        { iso: 7, name: "So" },
      ],
      weekSummary: null,
      auto: {
        enabled:      false,
        weekdays:     [1, 2, 3, 4, 5],
        time_from:    "09:00",
        time_to:      "17:00",
        duration:     30,
        booked_total: 0,
        weeks_ahead:  3,
      },
      autoConfig: { enabled: false, last_run: "", last_result: null, weeks_ahead: 3, booked_total: 0 },
      autoBusy: false,
      flash: "",
      vm: {
        calls: [],
        counts: {},
        status: "offen",
        q: "",
        loading: false,
        error: "",
      },
      vmNoteDrafts: {},
      vmStatusTabs: [
        { key: "offen",     label: "Offen" },
        { key: "neu",       label: "Neu" },
        { key: "in_arbeit", label: "In Arbeit" },
        { key: "erledigt",  label: "Erledigt" },
        { key: "alle",      label: "Alle" },
      ],
      vmTimer: null,
      mailUnread: 0,
      vmGreeting: "",
      vmGreetingSaving: false,
    };
  },
  mounted() {
    this.loadBookings();
    this.loadVm();
    this.loadMailUnread();
    // Polling: Badges immer aktuell halten; die Anruf-Liste nur, wenn der
    // Tab offen ist (Notiz-Entwürfe bleiben dabei erhalten).
    this.vmTimer = setInterval(() => {
      this.loadVm(true);
      this.loadMailUnread();
    }, 25000);
  },
  beforeUnmount() {
    if (this.vmTimer) clearInterval(this.vmTimer);
  },
  methods: {
    setTab(t) {
      this.tab = t;
      if (t === "slots") { this.loadSlots(); this.loadAuto(); }
      if (t === "bookings") this.loadBookings();
      if (t === "calls") { this.loadVm(); this.loadVmGreeting(); }
      if (t === "texts") this.loadTexts();
      if (t === "settings") this.loadSettings();
    },

    // ---- Anrufbeantworter-Ansage (cal_setting voicemail_greeting) ----
    async loadVmGreeting() {
      if (this.vmGreeting) return;
      const r = await BaseCalls.terminAdminGet({ action: "settings" });
      this.vmGreeting = r?.data?.settings?.voicemail_greeting || "";
    },
    async saveVmGreeting() {
      this.vmGreetingSaving = true;
      const r = await BaseCalls.terminAdminPost({
        action: "settings_update",
        settings: { voicemail_greeting: this.vmGreeting },
      });
      this.vmGreetingSaving = false;
      this.showFlash(r?.data?.success ? "Ansage gespeichert — gilt ab dem nächsten Anruf." : "Fehler beim Speichern.");
    },

    // ---- Anrufe (Anrufbeantworter) ----
    async loadVm(isPoll = false) {
      if (isPoll && this.tab !== "calls") {
        // Nur Zähler fürs Badge aktualisieren.
        try {
          const r = await BaseCalls.phoneVmList({ status: "neu", limit: 1 });
          if (r?.data?.counts) this.vm.counts = r.data.counts;
        } catch (_e) { /* Badge-Update darf still scheitern */ }
        return;
      }
      this.vm.loading = true;
      try {
        const r = await BaseCalls.phoneVmList({
          status: this.vm.status,
          q: this.vm.q || "",
          limit: 100,
        });
        if (r?.data?.success) {
          this.vm.error = "";
          this.vm.calls = r.data.calls || [];
          this.vm.counts = r.data.counts || {};
          // Notiz-Entwürfe erhalten: nur fehlende Keys aus der DB übernehmen.
          for (const c of this.vm.calls) {
            if (!(c.call_id in this.vmNoteDrafts)) {
              this.vmNoteDrafts[c.call_id] = c.note || "";
            }
          }
        } else {
          this.vm.error = r?.data?.detail || r?.data?.error || "Fehler beim Laden.";
        }
      } catch (_e) {
        this.vm.error = "Netzwerk-/Serverfehler.";
      } finally {
        this.vm.loading = false;
      }
    },
    vmStatusLabel(s) {
      return ({ neu: "neu", in_arbeit: "in Arbeit", erledigt: "erledigt" })[s] || s;
    },
    async loadMailUnread() {
      try {
        const r = await BaseCalls.mailGet("unread");
        if (r?.data?.success) this.mailUnread = r.data.unread;
      } catch (_e) { /* Badge darf still scheitern */ }
    },
    fmtVmTime(v) {
      if (!v) return "";
      const d = new Date(String(v).replace(" ", "T"));
      if (isNaN(d)) return String(v);
      return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" })
        + " " + d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
    },
    fmtDur(sec) {
      const m = Math.floor(sec / 60), s = sec % 60;
      return m ? `${m}:${String(s).padStart(2, "0")} Min.` : `${s} Sek.`;
    },
    async vmSetStatus(c, status) {
      const r = await BaseCalls.phoneVmPost({ action: "setStatus", call_id: c.call_id, status });
      if (r?.data?.success) {
        this.showFlash(status === "erledigt" ? "Als erledigt markiert." : "Status gespeichert.");
      }
      this.loadVm();
    },
    async vmSaveNote(c) {
      const note = this.vmNoteDrafts[c.call_id] || "";
      const r = await BaseCalls.phoneVmPost({ action: "setNote", call_id: c.call_id, note });
      this.showFlash(r?.data?.success ? "Notiz gespeichert." : "Fehler beim Speichern.");
      this.loadVm();
    },
    /** Rückruf: erst sperren (gegen Doppelanrufe), dann Softphone öffnen. */
    async vmCall(c, force = false) {
      const r = await BaseCalls.phoneVmPost({ action: "lock", call_id: c.call_id, force: force ? 1 : 0 });
      if (!r?.data?.success) {
        if (r?.data?.error === "locked") {
          this.showFlash(`${r.data.lock_name || "Jemand"} ruft gerade an.`);
          this.loadVm();
          return;
        }
        this.showFlash("Anruf konnte nicht gestartet werden.");
        return;
      }
      this.phoneDialog = {
        open: true,
        phone: c.from_number,
        bookingId: 0,
        name: c.customer_no ? `Kunde ${c.customer_no}` : c.from_number,
      };
      this.loadVm();
    },
    async vmUnlock(c) {
      await BaseCalls.phoneVmPost({ action: "unlock", call_id: c.call_id });
      this.loadVm();
    },
    statusLabel(s) {
      return ({ 0: "neu", 1: "bestätigt", 2: "abgesagt" })[s] || s;
    },
    showFlash(msg) {
      this.flash = msg;
      setTimeout(() => (this.flash = ""), 2500);
    },
    async logout() {
      const userStore = useUserStore();
      await userStore.logout();
      this.$router.replace({ name: "login" });
    },

    // ---- Slots ----
    async loadSlots() {
      const r = await BaseCalls.terminAdminGet({
        action: "slots",
        query: {
          from: this.slotRange.from + " 00:00:00",
          to:   this.slotRange.to   + " 23:59:59",
        },
      });
      this.slots = r?.data?.slots || [];
    },
    async addSlot() {
      const { date, time, duration, note, markBooked } = this.newSlot;
      if (!date || !time) return;
      const r = await BaseCalls.terminAdminPost({
        action: "slot_add",
        slot_start: `${date} ${time}:00`,
        slot_duration_min: duration || 30,
        slot_note: note || "",
        mark_booked: markBooked ? 1 : 0,
      });
      this.showFlash(r?.data?.success
        ? (markBooked ? "Slot angelegt und als gebucht markiert." : "Slot angelegt.")
        : (r?.data?.message || "Fehler")
      );
      this.loadSlots();
    },
    async addBulk() {
      const { dateFrom, dateTo, start, end, duration, weekdays } = this.bulk;
      if (!dateFrom || !dateTo || !start || !end || !duration) {
        this.showFlash("Bitte alle Felder ausfüllen.");
        return;
      }
      if (!weekdays || !weekdays.length) {
        this.showFlash("Bitte mindestens einen Wochentag auswählen.");
        return;
      }
      if (dateFrom > dateTo) {
        this.showFlash("Von-Datum muss vor Bis-Datum liegen.");
        return;
      }

      const slots = [];
      const [sh, sm] = start.split(":").map(Number);
      const [eh, em] = end.split(":").map(Number);
      const dayStartMin = sh * 60 + sm;
      const dayEndMin   = eh * 60 + em;
      if (dayStartMin + duration > dayEndMin) {
        this.showFlash("Zeitraum ist kürzer als ein Slot.");
        return;
      }

      const start_d = new Date(dateFrom + "T00:00:00");
      const end_d   = new Date(dateTo   + "T00:00:00");
      for (let d = new Date(start_d); d <= end_d; d.setDate(d.getDate() + 1)) {
        if (!weekdays.includes(d.getDay())) continue;
        const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        for (let mins = dayStartMin; mins + duration <= dayEndMin; mins += duration) {
          const h = String(Math.floor(mins / 60)).padStart(2, "0");
          const m = String(mins % 60).padStart(2, "0");
          slots.push({
            slot_start: `${iso} ${h}:${m}:00`,
            slot_duration_min: duration,
            slot_note: "",
          });
        }
      }

      if (!slots.length) {
        this.showFlash("Keine Slots zum Generieren — bitte Auswahl prüfen.");
        return;
      }

      const r = await BaseCalls.terminAdminPost({ action: "slot_add_bulk", slots });
      const data = r?.data || {};
      this.bulkSummary = {
        created: data.created ?? 0,
        skipped: data.skipped ?? [],
        errors:  data.errors  ?? [],
      };
      const parts = [`${this.bulkSummary.created} angelegt`];
      if (this.bulkSummary.skipped.length) parts.push(`${this.bulkSummary.skipped.length} übersprungen`);
      if (this.bulkSummary.errors.length)  parts.push(`${this.bulkSummary.errors.length} Fehler`);
      this.showFlash(parts.join(" · "));
      this.loadSlots();
    },
    /** Woche vorbereiten: Default-Slots anlegen + zufällig belegen. */
    async prepareWeek() {
      const { start, timeFrom, timeTo, duration, weekdays, bookedTotal } = this.week;
      if (!start) {
        this.showFlash("Bitte ein Datum in der gewünschten Woche wählen.");
        return;
      }
      if (!weekdays.length) {
        this.showFlash("Bitte mindestens einen Wochentag auswählen.");
        return;
      }
      const r = await BaseCalls.terminAdminPost({
        action:       "week_prepare",
        week_start:   start,
        time_from:    timeFrom,
        time_to:      timeTo,
        duration:     duration || 30,
        weekdays,
        booked_total: bookedTotal || 0,
      });
      const data = r?.data || {};
      if (!data.success) {
        this.showFlash(data.message || "Fehler beim Vorbereiten der Woche.");
        return;
      }
      this.weekSummary = data;
      this.showFlash(`${data.created} Slots angelegt · ${data.booked_marked} zufällig belegt`);
      // Slot-Liste direkt auf die vorbereitete Woche stellen.
      this.slotRange.from = data.week_start;
      const endD = new Date(data.week_start + "T00:00:00");
      endD.setDate(endD.getDate() + 6);
      this.slotRange.to = localIso(endD);
      this.loadSlots();
    },
    // ---- Automatik: jede Woche feste Zeiten + Belegung ----
    applyAutoConfig(cfg) {
      if (!cfg) return;
      this.autoConfig = cfg;
      this.auto = {
        enabled:      !!cfg.enabled,
        weekdays:     Array.isArray(cfg.weekdays) && cfg.weekdays.length ? [...cfg.weekdays] : [1, 2, 3, 4, 5],
        time_from:    cfg.time_from    || "09:00",
        time_to:      cfg.time_to      || "17:00",
        duration:     Number(cfg.duration)     || 30,
        booked_total: Number(cfg.booked_total) || 0,
        weeks_ahead:  Number(cfg.weeks_ahead)  || 3,
      };
    },
    async loadAuto() {
      const r = await BaseCalls.terminAdminGet({ action: "auto_prepare" });
      this.applyAutoConfig(r?.data?.config);
    },
    async saveAuto() {
      if (this.auto.enabled && !this.auto.weekdays.length) {
        this.showFlash("Bitte mindestens einen Wochentag auswählen.");
        return;
      }
      this.autoBusy = true;
      const r = await BaseCalls.terminAdminPost({ action: "auto_prepare_save", ...this.auto });
      this.autoBusy = false;
      const data = r?.data || {};
      if (!data.success) {
        this.showFlash(data.message || "Fehler beim Speichern der Automatik.");
        return;
      }
      this.applyAutoConfig(data.config);
      this.showFlash(this.auto.enabled
        ? "Automatik gespeichert und aktiv — läuft einmal täglich."
        : "Automatik gespeichert (aus).");
    },
    /** Automatik sofort mit den GESPEICHERTEN Werten ausführen. */
    async runAuto() {
      this.autoBusy = true;
      const r = await BaseCalls.terminAdminPost({ action: "auto_prepare_run" });
      this.autoBusy = false;
      const data = r?.data || {};
      if (!data.success) {
        this.showFlash(data.message || "Fehler beim Ausführen der Automatik.");
        return;
      }
      this.applyAutoConfig(data.config);
      const t = data.totals || {};
      this.showFlash(`Automatik gelaufen: ${t.created || 0} Slots angelegt · ${t.booked_marked || 0} belegt`);
      this.loadSlots();
    },
    /** Als belegt markierten Slot wieder freigeben. */
    async releaseSlot(s) {
      const r = await BaseCalls.terminAdminPost({ action: "slot_release", slot_id: s.slot_id });
      this.showFlash(r?.data?.success ? "Slot wieder freigegeben." : (r?.data?.message || "Fehler."));
      this.loadSlots();
    },
    async blockSlot(s) {
      await BaseCalls.terminAdminPost({ action: "slot_block", slot_id: s.slot_id });
      this.loadSlots();
    },
    async unblockSlot(s) {
      await BaseCalls.terminAdminPost({ action: "slot_unblock", slot_id: s.slot_id });
      this.loadSlots();
    },
    async deleteSlot(s) {
      if (!confirm("Slot wirklich löschen?")) return;
      await BaseCalls.terminAdminPost({ action: "slot_delete", slot_id: s.slot_id });
      this.loadSlots();
    },

    // ---- Telefon ----
    openDetail(b) {
      this.$router.push({ name: "admin-termin-detail", params: { id: b.booking_id } });
    },
    openPhone(b) {
      this.phoneDialog = {
        open: true,
        phone: b.phone || "",
        bookingId: b.booking_id || 0,
        name: b.name || "",
      };
    },

    // ---- Bookings ----
    async loadBookings() {
      const r = await BaseCalls.terminAdminGet({ action: "bookings" });
      this.bookings = r?.data?.bookings || [];
    },
    async confirmBooking(b) {
      await BaseCalls.terminAdminPost({ action: "booking_confirm", booking_id: b.booking_id });
      this.loadBookings();
    },
    async cancelBooking(b) {
      if (!confirm("Buchung absagen und Absage-Mail senden?")) return;
      await BaseCalls.terminAdminPost({
        action: "booking_cancel", booking_id: b.booking_id, send_mail: true,
      });
      this.loadBookings();
    },
    async deleteBooking(b) {
      if (!confirm("Buchung endgültig löschen?")) return;
      await BaseCalls.terminAdminPost({ action: "booking_delete", booking_id: b.booking_id });
      this.loadBookings();
    },

    // ---- Texts ----
    async loadTexts() {
      const r = await BaseCalls.terminAdminGet({ action: "texts" });
      this.texts = r?.data?.texts || [];
    },
    async saveText(t) {
      const r = await BaseCalls.terminAdminPost({
        action: "text_update",
        text_id: t.text_id,
        text_subject: t.text_subject || "",
        text_body: t.text_body || "",
      });
      this.showFlash(r?.data?.success ? "Gespeichert." : "Fehler beim Speichern.");
    },

    // ---- Settings ----
    async loadSettings() {
      const r = await BaseCalls.terminAdminGet({ action: "settings" });
      this.settings = r?.data?.settings || {};
    },
    async saveSettings() {
      const r = await BaseCalls.terminAdminPost({
        action: "settings_update",
        settings: this.settings,
      });
      this.showFlash(r?.data?.success ? "Einstellungen gespeichert." : "Fehler.");
    },
  },
};
</script>

<style scoped>
.admin-termin { max-width: 1100px; margin: 0 auto; padding: 24px 16px; }
.admin-head__row { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.admin-head h1 { margin: 0 0 12px; }
.admin-logout { flex-shrink: 0; }
.admin-tabs { display: flex; gap: 4px; flex-wrap: wrap; border-bottom: 1px solid #ddd; }
.admin-tabs button {
  border: none; background: none; padding: 10px 14px; cursor: pointer;
  border-bottom: 3px solid transparent; color: #444;
}
.admin-tabs button.active { border-bottom-color: #2a6bf2; color: #2a6bf2; font-weight: 600; }

.admin-section { padding: 16px 0; }
.admin-toolbar { display: flex; gap: 12px; align-items: flex-end; margin-bottom: 16px; flex-wrap: wrap; }
.admin-toolbar label { display: flex; flex-direction: column; font-size: .9em; gap: 4px; }
.admin-newslot {
  display: flex; gap: 10px; align-items: flex-end; flex-wrap: wrap;
  background: #f7f9fd; padding: 12px; border-radius: 8px; margin-bottom: 16px;
}
.admin-newslot label { display: flex; flex-direction: column; font-size: .85em; gap: 3px; }
.admin-newslot input { padding: 6px 8px; border: 1px solid #ccd; border-radius: 4px; }
.admin-newslot__check { flex-direction: row !important; align-items: center; gap: 6px !important; }
.admin-newslot__check input { width: auto; }
.admin-newslot--bulk { margin-bottom: 8px; }

.admin-weekdays {
  display: flex; align-items: center; flex-wrap: wrap; gap: 10px;
  background: #f7f9fd; padding: 10px 12px; border-radius: 8px; margin-bottom: 16px;
}
.admin-weekdays__label { font-size: .9em; font-weight: 600; margin-right: 4px; }
.admin-weekday {
  display: inline-flex; align-items: center; gap: 4px;
  font-size: .9em; cursor: pointer; padding: 2px 6px;
}
.admin-weekday input { cursor: pointer; }
.admin-weekdays__action { margin-left: auto; }

.admin-bulk-summary {
  background: #fff8e8; border-left: 4px solid #f59e0b;
  padding: 10px 14px; border-radius: 6px; margin-bottom: 16px; font-size: .9em;
}
.admin-bulk-summary p { margin: 4px 0; }
.admin-bulk-summary__list { color: #666; word-break: break-word; }
.admin-bulk-summary__err { color: #c0392b; }
.admin-auto__switch { align-self: end; }
.admin-auto__status { margin-bottom: 24px; }

.btn {
  padding: 6px 12px; border: 1px solid #ccd; background: #fff; border-radius: 4px;
  cursor: pointer; font: inherit;
}
.btn:disabled { opacity: .5; cursor: not-allowed; }
.btn--primary { background: #2a6bf2; color: white; border-color: #2a6bf2; }
.btn--danger  { color: #c0392b; border-color: #f0c8c4; }
.btn--call    { background: #16a34a; color: #fff; border-color: #16a34a; font-weight: 600; }
.btn--call:hover { background: #15803d; }
.btn--sm { padding: 4px 8px; font-size: .85em; }

.admin-table {
  width: 100%; border-collapse: collapse; background: #fff;
  border: 1px solid #e3e7ef; border-radius: 8px; overflow: hidden;
}
.admin-table th, .admin-table td {
  text-align: left; padding: 8px 10px; border-bottom: 1px solid #eef;
  font-size: .92em; vertical-align: top;
}
.admin-table tr.is-booked-red {
  background: #fdecea;
  color: #b91c1c;
}
.admin-table tr.is-booked-red td { border-bottom-color: #f5c6c0; }
.admin-table tr.is-blocked { background: #fafafa; color: #888; }
.booking-row { cursor: pointer; transition: background .12s ease; }
.booking-row:hover { background: #f4f8ff; }
.booking-row__name { font-weight: 700; color: #143a86; white-space: nowrap; }
.booking-row__sub { display: block; font-weight: 400; color: #778; font-size: .82em; }
.booking-row__arrow { opacity: 0; transition: opacity .12s ease; color: #2a6bf2; }
.booking-row:hover .booking-row__arrow { opacity: 1; }
.status-booked { color: #b91c1c; font-weight: 700; }
.td-actions { white-space: nowrap; display: flex; gap: 4px; flex-wrap: wrap; }
.td-msg { max-width: 280px; }

.source-badge {
  display: inline-block; background: #eef5ff; color: #143a86;
  border: 1px solid #c9dcf8; border-radius: 999px;
  padding: 2px 10px; font-size: .85em; white-space: nowrap;
}

/* ---- Anrufe-Tab ---- */
.tab-calls { position: relative; }
.tab-calls__badge {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 20px; height: 20px; padding: 0 6px; margin-left: 6px;
  background: #c0392b; color: #fff; border-radius: 999px;
  font-size: 11px; font-weight: 900;
  animation: vm-blink 1.2s ease-in-out infinite;
}
@keyframes vm-blink {
  0%, 100% { opacity: 1; }
  50% { opacity: .45; }
}
.vm-greeting {
  background: #fff; border: 1px solid #e3e7ef; border-radius: 10px;
  padding: 10px 14px; margin-bottom: 14px;
}
.vm-greeting summary { cursor: pointer; font-weight: 700; color: #143a86; }
.vm-greeting__text {
  width: 100%; font: inherit; font-size: .92em; line-height: 1.55;
  border: 1px solid #ccd; border-radius: 8px; padding: 10px 12px; margin: 10px 0;
}
.vm-chips { display: flex; gap: 6px; flex-wrap: wrap; }
.vm-chip {
  border: 1px solid #ccd; background: #fff; border-radius: 999px;
  padding: 5px 12px; cursor: pointer; font: inherit; font-size: .88em;
  display: inline-flex; align-items: center; gap: 6px;
}
.vm-chip--active { background: #2a6bf2; border-color: #2a6bf2; color: #fff; }
.vm-chip__count {
  background: rgba(0,0,0,.08); border-radius: 999px;
  padding: 0 7px; font-size: .85em; font-weight: 700;
}
.vm-chip--active .vm-chip__count { background: rgba(255,255,255,.25); }
.vm-search {
  flex: 1; min-width: 180px; max-width: 320px;
  padding: 7px 10px; border: 1px solid #ccd; border-radius: 999px; font: inherit;
}

.vm-card {
  background: #fff; border: 1px solid #e3e7ef; border-radius: 10px;
  padding: 14px 16px; margin-bottom: 12px;
}
.vm-lock {
  display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
  border-radius: 8px; padding: 8px 12px; margin-bottom: 10px;
  font-size: .9em; font-weight: 700;
}
.vm-lock--me    { background: #e8f7ee; color: #1d7a43; border: 1px solid #bfe6cd; }
.vm-lock--other { background: #fdecea; color: #b91c1c; border: 1px solid #f5c6c0; }
.vm-card__head {
  display: flex; justify-content: space-between; align-items: baseline;
  gap: 10px; flex-wrap: wrap;
}
.vm-card__who { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.vm-card__number { font-size: 1.05em; }
.vm-card__meta { color: #667; font-size: .88em; }
.vm-status {
  border-radius: 999px; padding: 2px 10px;
  font-size: .78em; font-weight: 900; text-transform: uppercase; letter-spacing: .5px;
}
.vm-status--neu       { background: #fdecea; color: #b91c1c; }
.vm-status--in_arbeit { background: #fff8e8; color: #92610a; }
.vm-status--erledigt  { background: #e8f7ee; color: #1d7a43; }
.vm-card__wish {
  margin: 8px 0 0; font-size: .92em; color: #92610a;
  background: #fff8e8; border-radius: 6px; padding: 6px 10px; display: inline-block;
}
.vm-card__transcript {
  margin: 10px 0; padding: 10px 12px;
  background: #f7f9fd; border-radius: 8px;
  font-size: .93em; line-height: 1.55; white-space: pre-wrap;
}
.vm-card__transcript em { color: #889; }
.vm-card__err { color: #b91c1c !important; }
.vm-card__actions { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
.btn--call { background: #1d7a43; border-color: #1d7a43; color: #fff; font-weight: 700; }
.btn--call:hover:not(:disabled) { background: #16613445; }
.vm-select {
  padding: 4px 8px; border: 1px solid #ccd; border-radius: 4px; font: inherit; font-size: .85em;
}
.vm-card__note { display: flex; gap: 8px; align-items: flex-end; margin-top: 10px; }
.vm-card__note textarea {
  flex: 1; border: 1px solid #ccd; border-radius: 6px; padding: 8px; font: inherit; font-size: .9em;
}

.admin-empty { padding: 24px; text-align: center; color: #666; }

.admin-text {
  background: #fff; border: 1px solid #e3e7ef; border-radius: 8px;
  padding: 14px; margin-bottom: 14px;
}
.admin-text header { margin-bottom: 8px; }
.admin-text header small { color: #888; margin-left: 6px; }
.admin-text label { display: flex; flex-direction: column; font-size: .9em; gap: 4px; margin-bottom: 8px; }
.admin-text input, .admin-text textarea {
  border: 1px solid #ccd; border-radius: 4px; padding: 8px; font: inherit;
}

.admin-setting label { display: flex; flex-direction: column; max-width: 480px; margin-bottom: 12px; }
.admin-setting__label { font-size: .85em; color: #555; }

.admin-help { background: #eef5ff; padding: 10px; border-radius: 6px; font-size: .85em; }
.admin-help code { background: #fff; padding: 1px 4px; border-radius: 3px; }

.admin-flash {
  position: fixed; bottom: 24px; right: 24px;
  background: #143a86; color: white; padding: 10px 16px; border-radius: 6px;
  box-shadow: 0 6px 18px rgba(0,0,0,.15);
}
</style>
